import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api, euro } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setData(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current > 12) {
        setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="pt-32 pb-24 min-h-[70vh] flex items-center">
      <div className="mx-auto max-w-2xl w-full px-5 lg:px-10 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={48} className="mx-auto text-gold animate-spin" />
            <h1 className="mt-8 font-serif text-3xl text-white">Confirmation du paiement…</h1>
            <p className="mt-3 text-white/60">Merci de patienter quelques instants.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 size={56} className="mx-auto text-gold" />
            <h1 className="mt-8 font-serif text-4xl text-white">Réservation confirmée</h1>
            <p className="mt-3 text-white/60">Votre paiement a bien été reçu. Un chauffeur vous sera attribué.</p>

            <div className="mt-10 rounded-2xl border border-gold/25 bg-panel p-8 text-left">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <span className="text-white/50 text-sm">Référence</span>
                <span className="gold-text font-serif text-2xl" data-testid="booking-reference">{data?.reference}</span>
              </div>
              {data?.booking && (
                <div className="mt-4 space-y-3 text-sm">
                  <Row k="Véhicule" v={data.booking.vehicle_name} />
                  <Row k="Départ" v={data.booking.pickup || "—"} />
                  {data.booking.trip_type !== "hourly" && <Row k="Arrivée" v={data.booking.dropoff || "—"} />}
                  <Row k="Date & heure" v={`${data.booking.date || ""} ${data.booking.time || ""}`} />
                  <Row k="Client" v={`${data.booking.customer?.first_name} ${data.booking.customer?.last_name}`} />
                  <div className="flex items-center justify-between border-t border-white/10 pt-4">
                    <span className="text-white/50">Total payé</span>
                    <span className="gold-text font-serif text-2xl">{euro(data.amount)}</span>
                  </div>
                </div>
              )}
            </div>

            <Link to="/" className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-medium text-black hover:bg-goldsoft transition-colors">
              Retour à l'accueil <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="payment-failed">
            <XCircle size={56} className="mx-auto text-red-400" />
            <h1 className="mt-8 font-serif text-3xl text-white">Paiement non confirmé</h1>
            <p className="mt-3 text-white/60">Nous n'avons pas pu confirmer votre paiement. Aucun montant n'a été débité si la transaction n'a pas abouti.</p>
            <Link to="/reservation" className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/60 px-8 py-3.5 text-sm font-medium text-gold hover:bg-gold/10 transition-colors">
              Réessayer la réservation
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-white/50">{k}</span>
    <span className="text-white/85 text-right">{v}</span>
  </div>
);
